import styles from "@styles/forms-flow/creative.module.scss";
import { useEffect, useState } from "react";

import {
  BaseCurrencyInput,
  Button,
  Checkbox,
  Hairline,
  Input,
  Label,
  Message,
  Radio,
} from "@components";
import { If } from "@components/control-statements";
import dayjs from "dayjs";
import { Environment } from "shared/core/Environment";

export const BenefitDetails = ({
  coupon,
  onChangeCreative,
  onEnableMaxGain,
}) => {
  const [sku, setSku] = useState("");

  const setMultipleSubitems = (data) => {
    onChangeCreative({
      ...(coupon || {}),
      multiple_items: data,
    });
  };

  useEffect(() => {
    if (coupon?.skus?.length < 2) setMultipleSubitems(false);
  }, [coupon?.skus]);

  const setExpiration = (data) => {
    onChangeCreative({
      expiration: {
        ...(coupon.expiration || {}),
        ...data,
      },
    });
  };

  const [maxGainEnabled, setMaxGainEnabled] = useState(coupon?.max_gain > 0);

  const enableMaxGain = (status) => {
    setMaxGainEnabled(status);
    onEnableMaxGain(status);
  };

  const addSKU = () => {
    const index = (coupon?.skus || [])?.indexOf(sku);

    if (index !== -1 || sku.length === 0) return;

    onChangeCreative({
      skus: [...(coupon?.skus || []), sku],
    });
    setSku("");
  };

  const calculateExpirationDate = (days = 0) => {
    const expirationDate = dayjs().add(days, "day");

    return expirationDate.format("DD/MM/YYYY");
  };

  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  const isPercentageDiscount = (type) =>
    ["PERCENTAGE_DISCOUNT", "CASHBACK", "PRODUCT_DISCOUNT"].includes(type);

  return (
    <>
      <Label>Insira o código do benefício</Label>
      <Input
        placeholder="Código do benefício"
        autoFocus
        value={coupon?.code}
        max={15}
        onChange={(text) =>
          onChangeCreative({
            code: (text.target.value || "")
              .toUpperCase()
              .replace(/[^A-Z0-9]/gi, ""),
          })
        }
      />

      <If cond={coupon?.code?.length == 15}>
        <Message className="mt-10">Limite de 15 caracteres</Message>
      </If>
      <Hairline className="mt-30 mb-30" />

      <If
        cond={["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(
          coupon?.type
        )}
      >
        <Label text={"Adicione SKU's"} />
        <Input
          className="mb-0"
          value={sku}
          onChange={(text) => setSku(text.target.value.trim() || "")}
          placeholder="000000000000000"
        />
        <Button onClick={addSKU} design="three" className="w-100 mt-20 mb-20">
          Adicionar
        </Button>

        <Label text={`SKU's (${coupon?.skus?.length || 0})`} />
        <div className={styles.skus}>
          {coupon?.skus?.length > 0 ? (
            coupon?.skus?.map((sku) => {
              return (
                <div className={styles.sku} key={sku}>
                  <p>{sku}</p>
                  <div
                    className={styles.remove}
                    onClick={() =>
                      onChangeCreative({
                        skus: coupon?.skus.filter((s) => s !== sku),
                      })
                    }
                  >
                    <i className="far fa-times" />
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.emptySKUS}>
              <div className={styles.icon}>
                <i className="fal fa-folder-open" />
              </div>
              <p>Nenhum sku foi adicionado</p>
            </div>
          )}
        </div>
        <Checkbox
          className="mt-20 mb-20"
          disabled={!coupon?.skus || coupon?.skus?.length <= 1}
          checked={coupon?.multiple_items}
          onChange={() => setMultipleSubitems(!coupon?.multiple_items)}
        >
          Permitir múltiplos subitens?
        </Checkbox>
        {!coupon?.multiple_items ? (
          <Message text="O cupom será liberado para uso somente quando um único SKU selecionado no produto esteja dentro dos subitems liberados no cupom, aplicando desconto em todas as unidades do subitem, mas somente em uma unidade do produto." />
        ) : (
          <Message text="O cupom será liberado para uso quando um ou mais SKU's selecionados no produto estejam dentro dos subitems liberados no cupom, aplicando desconto em todas as unidades do subitem, mas somente em uma unidade do produto." />
        )}
        <Hairline className="mt-30 pt-20 mb-10" />
      </If>

      <div className="d-flex flex-column mt-30">
        <Label text="Insira o valor e o pedido mínimo" />
        <div className="d-flex w-100">
          <If cond={coupon.type != "FREE_DELIVERY"}>
            <If
              cond={
                (coupon.type == "CASHBACK" && !programByPoint) ||
                coupon.type != "CASHBACK"
              }
            >
              <BaseCurrencyInput
                design="two"
                label={
                  coupon?.type === "PRODUCT_DISCOUNT"
                    ? "Defina o desconto"
                    : "Valor do benefício"
                }
                className="w-100 mr-15 mb-0"
                value={coupon.value}
                prefix={isPercentageDiscount(coupon?.type) ? "" : "R$ "}
                suffix={isPercentageDiscount(coupon?.type) ? "%" : ""}
                onChange={(v) => onChangeCreative({ ...coupon, value: v })}
              />
              <BaseCurrencyInput
                design="two"
                label="Pedido mínimo"
                className="w-100 mb-0"
                value={coupon.min_order}
                onChange={(v) => onChangeCreative({ ...coupon, min_order: v })}
              />
            </If>
            <If cond={programByPoint && coupon.type == "CASHBACK"}>
              <div className="d-flex flex-column w-100">
                <div className="d-flex align-center mb-15 mt-10">
                  <BaseCurrencyInput
                    design="two"
                    className="w-100 mb-0 pt-0"
                    suffix={" gastos"}
                    value={100}
                    disabled
                  />
                  <span className={styles.equal}>=</span>
                  <BaseCurrencyInput
                    design="two"
                    suffix={` ${coupon.value === 1 ? "ponto" : "pontos"}`}
                    prefix={""}
                    precision={0}
                    className="w-100 mb-0 pt-0"
                    value={coupon.value || 0}
                    onChange={(percentage) => {
                      onChangeCreative({
                        ...coupon,
                        value: percentage,
                      });
                    }}
                  />
                </div>
                <BaseCurrencyInput
                  design="two"
                  label="Pedido mínimo"
                  className="w-100 mb-0"
                  value={coupon.min_order}
                  onChange={(v) =>
                    onChangeCreative({ ...coupon, min_order: v })
                  }
                />
              </div>
            </If>
          </If>
          <If cond={coupon.type == "FREE_DELIVERY"}>
            <BaseCurrencyInput
              design="two"
              label="Pedido mínimo"
              className="w-100"
              value={coupon.min_order}
              onChange={(v) => onChangeCreative({ ...coupon, min_order: v })}
            />
          </If>
        </div>
      </div>

      {coupon?.type != "CASHBACK" && (
        <>
          <Hairline className="mt-20 mb-30" />
          <p className={styles.label}>Defina a expiração do benefício</p>
          <BaseCurrencyInput
            className="w-100"
            suffix={" dia(s)"}
            prefix={""}
            precision={0}
            value={coupon?.expiration?.gain?.days}
            onChange={(n) =>
              setExpiration({
                gain: {
                  ...(coupon?.expiration?.gain || {}),
                  days: n,
                },
              })
            }
          />
        </>
      )}

      {coupon?.type == "CASHBACK" && (
        <>
          <Hairline className="mt-30 mb-30" />
          <p className={styles.label}>
            Deseja configurar limite de ganho do cashback?
          </p>
          <Radio
            family="max-gain"
            className="mb-10"
            value={true}
            checked={coupon?.max_gain > 0 || maxGainEnabled}
            onChange={() => {
              enableMaxGain(true);
              onChangeCreative({ max_gain: 0 });
            }}
          >
            Sim
          </Radio>
          <Radio
            family="max-gain"
            value={false}
            checked={maxGainEnabled}
            onChange={() => {
              enableMaxGain(false);
              onChangeCreative({ max_gain: 0 });
            }}
          >
            Não
          </Radio>

          {maxGainEnabled && (
            <>
              <div className="mt-20" />
              <p className={styles.label}>
                Determine o limite de ganho do cashback
              </p>
              <If cond={programByPoint}>
                <BaseCurrencyInput
                  design="two"
                  suffix={` ${coupon.max_gain === 1 ? "ponto" : "pontos"}`}
                  prefix={""}
                  precision={0}
                  className="w-100 mb-0 pt-0"
                  value={coupon.max_gain || 0}
                  onChange={(v) => {
                    onChangeCreative({
                      ...coupon,
                      max_gain: v,
                    });
                  }}
                />
              </If>
              <If cond={!programByPoint}>
                <BaseCurrencyInput
                  label="Limite de Ganho"
                  className="w-100"
                  design="two"
                  value={coupon?.max_gain}
                  onChange={(n) => onChangeCreative({ max_gain: n })}
                />
              </If>
            </>
          )}

          <Hairline className="mt-30 mb-30" />
          <p className={styles.label}>
            Defina a expiração de ganho deste benefício
          </p>
          <BaseCurrencyInput
            className="mb-20 w-100"
            placeholder="--- dias de expiração de ganho"
            type="number"
            prefix={""}
            precision={0}
            suffix={" dias de expiração de ganho"}
            value={coupon?.expiration?.gain?.days}
            onChange={(n) =>
              setExpiration({
                gain: {
                  ...(coupon?.expiration?.gain || {}),
                  days: n,
                },
              })
            }
          />
          {/* <BaseCurrencyInput
						className='w-100 pt-0'
						placeholder='--- dias de expiração de gasto'
						prefix={''}
						precision={0}
						suffix={' dias de expiração de gasto'}
						value={coupon?.expiration?.spend?.days}
						onChange={(n) =>
							setExpiration({
								spend: { ...(coupon?.expiration?.spend || {}), days: n },
							})
						}
					/> */}
          <Message>
            A expiração deste benefício inicia a partir do momento que o usuário
            o recebe. Se adicionado à carteira agora, o cupom expirará em{" "}
            {calculateExpirationDate(coupon?.expiration?.gain?.days)} às 23:59.
          </Message>
        </>
      )}

      {["PRODUCT_DISCOUNT", "PRODUCT_CASH_DISCOUNT"].includes(coupon?.type) && (
        <>
          <Hairline className="mt-30 mb-30" />
          <Label text={"Defina o nome do produto no cupom"} />
          <Input
            value={coupon?.description}
            onChange={(text) =>
              onChangeCreative({ description: text.target.value })
            }
            max={50}
          />
        </>
      )}

      <If cond={Environment.id == 34}>
        <Hairline />
        <Input
          design="two"
          label="Grupo (3SCheckout)"
          className="mb-20"
          value={coupon?.group}
          onChange={(e) => onChangeCreative({ group: e.target.value || "" })}
        />
        <Input
          design="two"
          label="Justificativa (3SCheckout)"
          value={coupon?.justification}
          onChange={(e) =>
            onChangeCreative({ justification: e.target.value || "" })
          }
        />
      </If>
    </>
  );
};
